import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { LinearProgress } from "@mui/material";
import CustomNoRowsOverlay from "../utils/styles"; // Ensure this is properly imported
import ActionButton from "../components/Forms/ActionButton";
import { toast } from "react-toastify";
import * as api from "../redux/api";
import { reactIcons } from "../utils/icons";
import ChangeUserPassword from "../components/modals/ChangeUserPassword";
import UserKYCTable from "../components/modals/UserKYC";
import AddUser from "../components/modals/AddUser";
import { useSelector } from "react-redux";
import { debounce } from "lodash";
import EditUser from "../components/modals/EditUser";
import CustomDatePicker from "../components/Common/CustomDatePicker";

const Users = () => {
  const [open, setOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState({});
  const [editOpen, setEditOpen] = useState(false);

  const [queryParams, setQueryParams] = useState({});
  const [userData, setUserData] = React.useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [rows, setRows] = useState([]);
  const [activeType, setActiveType] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [openUserKYCTable, setOpenUserKYCTable] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const user = useSelector((state) => state.auth.user);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 9
  });
  const [rowCountState, setRowCountState] = useState(0);

  const handleSelectChange = (e) => {
    const selectedValue = e.target.value;
    setActiveType(selectedValue);
    let queryParam = {};

    switch (selectedValue) {
      case "Active":
        queryParam = {
          userStatus: "Active"
        };
        break;
      case "Verified":
        queryParam = { isVerified: true };
        break;
      case "NotVerified":
        queryParam = { isVerified: false };
        break;
      case "Blocked":
        queryParam = { userStatus: "Blocked" };
        break;
      case "Suspend":
        queryParam = { userStatus: "Suspend" };
        break;
      default:
        queryParam = {};
    }

    setQueryParams(queryParam);
  };

  const options = (userData) => {
    const userId = userData.id;
    const blockLabel = userData.status === "Blocked" ? "Unblock" : "Block";
    const suspendLabel =
      userData.status === "Suspended" ? "Reinstate" : "Suspend";
    return [
      {
        label: "Change Password",
        onClick: () => {
          setOpen(true);
          setUserData(userId);
        }
      },
      {
        label: "Edit User",
        onClick: () => {
          setEditOpen(true);
          setData(userData);
        }
      },

      {
        label: blockLabel,
        onClick: () => handleBlockAndSuspend(userId, blockLabel)
      },
      {
        label: suspendLabel,
        onClick: () => handleBlockAndSuspend(userId, suspendLabel)
      }
    ];
  };

  let columns = [
    {
      field: "_id",
      headerName: "S.No.",
      type: "number",
      headerAlign: "center",
      align: "center",
      width: 100
    },
    {
      field: "name",
      headerName: "Name",
      type: "string",
      flex: 2
    },
    {
      field: "email",
      headerName: "Email",
      type: "string",
      flex: 3,
      valueFormatter: (params) => (params.value ? params.value : "-")
    },

    {
      field: "type",
      headerName: "Type",
      type: "string",
      flex: 2,
      valueGetter: (params) => params.row?.Organization?.type
    },

    {
      field: "organizationName",
      headerName: "Organization",
      type: "string",
      valueGetter: (params) => params.row?.Organization?.name || "-",

      flex: 4
    },
    {
      field: "status",
      headerName: "Status",
      type: "string",
      flex: 2,
      valueFormatter: (params) => (params.value ? params.value : "-")
    },
    {
      field: "action",
      headerName: "Action",
      type: "actions",
      flex: 2,
      getActions: (params) => {
        return [
          <ActionButton key={params.row.id} options={options(params.row)} />
        ];
      }
    }
  ];
  const getUserData = useCallback(async () => {
    setIsLoading(true);
    const response = await api.getUserss(
      paginationModel.page,
      searchText,
      queryParams
      // userType
    );
    const { data, status } = response;
    if (status >= 200 && status < 300) {
      setIsLoading(false);
      setRowCountState(data.count);
      setRows(
        data.data.map((item, index) => {
          const kycStatus = `${item?.isVerified ? "Verified" : "Not Verified"}`;
          const address =
            `${item?.meta?.address_line_1 ?? ""}${
              item?.meta?.address_line_2
                ? ", " + item?.meta?.address_line_2
                : ""
            }${item?.meta?.city ? ", " + item?.meta?.city : ""}${
              item?.meta?.state ? ", " + item?.meta?.state : ""
            }` ?? " ";
          return {
            ...item,
            name: item?.firstname + " " + item?.lastname,
            email: item?.email === null ? "N/A" : item?.email,
            mobile: item?.mobile,
            address: address === "" ? "N/A" : address,
            walletAddress: item?.meta?.walletAddress,
            status: item?.status === "Suspend" ? "Suspended" : item?.status,
            kycStatus: kycStatus,
            _id: 8 * paginationModel.page + index + 1,
            id: item?.id,
            isBlock: item?.isBlock,
            isSuspend: item?.isSuspend
          };
        })
      );
    } else {
      setIsLoading(false);
      toast.error(data.message);
    }
  }, [paginationModel, searchText, queryParams]);

  const debouncedFetchUsers = useMemo(() => {
    return debounce(getUserData, 500);
  }, [getUserData]);

  useEffect(() => {
    debouncedFetchUsers();
    return () => {
      debouncedFetchUsers.cancel();
    };
  }, [debouncedFetchUsers]);

  const handleBlockAndSuspend = async (id, blockLabel) => {
    setIsLoading(true);
    try {
      const res = await api.blockUserAndSuspendById(id, blockLabel);
      if (res.status === 200 || res.status === 201) {
        setIsLoading(false);
        toast.success(`${blockLabel} Successfully`);
        debouncedFetchUsers();
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      toast.error(error.message);
      setIsLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const formatDateOnly = (date) => {
        const d = new Date(date);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
      };

      let formattedStart = startDate ? formatDateOnly(startDate) : null;
      let formattedEnd = endDate ? formatDateOnly(endDate) : null;

      const response = await api.getCsv(formattedStart, formattedEnd);
      const contentType = response.headers["content-type"];
      if (!contentType.includes("spreadsheetml")) {
        const reader = new FileReader();
        reader.onload = () => {
          console.error("Unexpected response (not Excel):", reader.result);
        };
        reader.readAsText(response.data);
        return;
      }

      const blob = new Blob([response.data], { type: contentType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `KYC_Records_${formattedStart || "All"}_to_${
        formattedEnd || "All"
      }.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message);
      console.error(err);
    }
  };

  return (
    <>
      <div className="w-full mt-10 data-grid">
        <div className="mb-6">
          <div className="flex justify-between items-center">
            <div className="flex gap-2 items-center">
              <h4 className="">Users</h4>
              <button
                type="button"
                className="btn btn-primary flex justify-between items-center "
                onClick={() => setIsOpen(true)}
              >
                <span className="text-black  text-22">
                  {reactIcons.addUser}
                </span>
                Add User
              </button>
            </div>
            <div className="flex flex-col justify-end items-end gap-2">
              <div className="flex gap-2">
                <div className="relative">
                  <input
                    className="w-[250px] h-[45px]"
                    type="text"
                    value={searchText}
                    placeholder="Search by name, email"
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                  <span className="absolute text-18 top-[calc(50%-9px)] right-2">
                    {reactIcons.search}
                  </span>
                </div>
                <div className="form-select-custom">
                  <select
                    className="min-w-[150px] w-full h-[45px] bg-white"
                    name="usersType"
                    id="usersType"
                    value={activeType}
                    onChange={handleSelectChange}
                  >
                    <option value="" disabled selected>
                      Status
                    </option>
                    <option value="">All</option>
                    <option value="Active">Active</option>

                    <option value="Blocked">Blocked</option>
                    <option value="Suspend">Suspend</option>
                  </select>
                  <span className="ay-center right-2 text-zinc-500 text-[20px] pointer-events-none ">
                    {reactIcons.arrowDown}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-end justify-end gap-4 my-4 ">
            <div className="">
              <CustomDatePicker
                label="Start Date"
                selectedDate={startDate}
                onChange={(date) => setStartDate(date)}
                disableFuture={true}
              />
            </div>
            <div className="">
              <CustomDatePicker
                label="End Date"
                selectedDate={endDate}
                onChange={(date) => setEndDate(date)}
                disableFuture={true}
                minDate={startDate}
              />
            </div>
            <button
              className="bg-primary-100 font-poppins text-white px-4 h-11 rounded"
              type="button"
              onClick={handleDownloadCSV}
              disabled={(startDate && !endDate) || (!startDate && endDate)}
            >
              Download CSV
            </button>
          </div>
        </div>

        <div className="h-[600px] w-full mt-4">
          <DataGrid
            loading={isLoading}
            rows={rows}
            getRowId={(row) => row._id}
            getEstimatedRowHeight={() => 52}
            columns={columns}
            initialState={{
              paginationModel: {
                pageSize: 8
              }
            }}
            rowCount={rowCountState}
            pageSizeOptions={[8]}
            paginationModel={paginationModel}
            paginationMode="server"
            onPaginationModelChange={setPaginationModel}
            slots={{
              loadingOverlay: LinearProgress,
              noRowsOverlay: CustomNoRowsOverlay
            }}
            disableColumnMenu
            disableRowSelectionOnClick
            sx={{
              fontSize: "100px", // <- Increase base font size
              "& .MuiDataGrid-cell": {
                fontSize: "50px"
              },
              "& .MuiDataGrid-columnHeaders": {
                fontSize: "17px",
                fontWeight: "00"
              },
              "& .MuiDataGrid-row:hover": {
                cursor: "default"
              }
            }}
          />
        </div>
      </div>
      <ChangeUserPassword
        open={open}
        handleClose={() => {
          setOpen(false);
          debouncedFetchUsers();
        }}
        userData={userData}
      />
      {openUserKYCTable && (
        <UserKYCTable
          open={openUserKYCTable}
          handleClose={() => {
            setOpenUserKYCTable(false);
            setSelectedUser(null);
          }}
          userData={selectedUser}
        />
      )}
      {isOpen && (
        <AddUser
          handleClose={() => {
            setIsOpen(false);
            debouncedFetchUsers();
          }}
          open={isOpen}
          user={user}
        />
      )}
      {editOpen && (
        <EditUser
          handleClose={() => {
            setEditOpen(false);
            debouncedFetchUsers();
          }}
          open={editOpen}
          user={user}
          data={data}
        />
      )}
    </>
  );
};

export default Users;
